import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProviderResource, UsageReadings } from 'src/app/models/usage-readings';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import Swal from 'sweetalert2';
import * as XLSX from "xlsx";


@Component({
  selector: 'app-usage-dialog',
  templateUrl: './usage-dialog.component.html',
  styleUrls: ['./usage-dialog.component.scss']
})
export class UsageDialogComponent implements OnInit {
  file: any;
  extension: string = '';
  jsonContent: UsageReadings[] = [];
  gasUsage: UsageReadings[] = [];
  electricUsage: UsageReadings[] = [];
  preparedJson: UsageReadings[] = [];
  result: any;
  accountNumber: Number = 0;
  viewUsage = false;
  usageType = '';
  spin = false;
  isFemdDom1: boolean = false;
  providerResource: ProviderResource = { Electric: '', Gas: '' };
  providerAccountNumber = '';
  templateDownload: string;
  basePath: string = "../../assets/" + this.utilityService.currentUtility.OfficeId +"/templateExcels/";
     
  constructor(public dialog: MatDialogRef<UsageDialogComponent>,
   private customerService: CustomerService,
   private utilityService: UtilityService,
   private userService: UserService,
   @Inject(MAT_DIALOG_DATA) public data: any
  ) { 
    this.templateDownload = `${this.basePath}MeterUsage.xlsx`;
    this.isFemdDom1 = userService.IsCustomUiChange;
  }
  
  ngOnInit(): void {
    this.spin = true;
    this.customerService.getProviderResources(this.data.accountNumber).subscribe(data => {
      this.spin = false;
      if (data?.length > 0) {
        const resource = data[0] as ProviderResource & { AccountNumber?: string };
        this.providerResource = resource;
        this.providerAccountNumber = resource.AccountNumber ?? '';
      }
    });
  }

  handleFiles(e: any) {
    this.file = e.target.files[0];
    this.extension = this.file ? this.file.name.split(".").pop() : '';
    const fileReader = new FileReader();
    fileReader.onload = (event: any) => {
      this.result = event.target.result;
    };
    fileReader.readAsDataURL(this.file);

    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(e.target.files[0]);
    reader.onload = (event: any) => {
      const binarystr: string = event.target.result;
      const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary", cellDates: true });

      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { dateNF: 'yyyy-mm-dd', raw: false });
      const normalizedRows = this.normalizeUsageRows(data as any[]);
      this.preparedJson = normalizedRows;

      if (this.usageType === 'Electric') {
        this.electricUsage = [...normalizedRows];
      }

      if (this.usageType === 'Gas') {
        this.gasUsage = [...normalizedRows];
      }

      if (this.usageType) {
        this.jsonContent = [...normalizedRows];
      }
    };
  }

  private normalizeUsageRows(data: any[]): UsageReadings[] {
    if (!data || data.length === 0) {
      return [];
    }

    const accountNumber = this.getCurrentAccountNumber();

    return data.map((row: any) => {
      const serialNo = row['Serial No.'] ?? row['SerialNo'] ?? row['Device'] ?? '';
      const periodStart = this.parseExcelDate(row['Period Start'] ?? row['PeriodStart']);
      const readDate = this.parseExcelDate(row['Read Date'] ?? row['ReadDate'] ?? row['MR Date']);
      const usage = row['Usage'] ?? row['Consumption'] ?? 0;
      const demand = row['Demand'] ?? null;
      const amount = row['Amount'] ?? null;
      const type = row['Type'] ?? row['Usage Type'] ?? this.usageType ?? 'Electric';

      return {
        AccountNumber: String(row['Account Number'] ?? row['AccountNo'] ?? accountNumber),
        SerialNo: String(serialNo),
        PeriodStart: periodStart,
        ReadDate: readDate,
        Usage: Number(usage) || 0,
        Demand: demand === null || demand === undefined ? 0 : Number(demand),
        Amount: amount === null || amount === undefined ? 0 : Number(amount),
        UsageType: String(type),
        Type: String(type)
      } as UsageReadings;
    });
  }

  private parseExcelDate(value: any): Date {
    if (!value) {
      return new Date();
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'number') {
      return new Date(Math.round((value - 25569) * 86400 * 1000));
    }

    return new Date(value);
  }

  private getCurrentAccountNumber(): string {
    return this.providerAccountNumber || this.data?.accountNumber || '';
  }

  prepareJSON(data: any[]) {
    this.preparedJson = [];
    data.forEach((row: any, index: number) => {
      const device = row['Device'] as string;
      const mrDate = new Date(row['MR Date'] as string);
      const consumption = Number(row['Consumption'] as string);
      const meterReading = {
        AccountNumber: this.getCurrentAccountNumber(),
        SerialNo: device,
        ReadDate: new Date(mrDate.getFullYear(), mrDate.getMonth(), mrDate.getDate()),
        Usage: consumption,
        PeriodStart: new Date(),
        Demand: 0,
        Amount: 0,
        UsageType: this.usageType || 'Electric'
      } as UsageReadings;

      if (index == data.length - 1) {
        meterReading.PeriodStart =  new Date(mrDate.getFullYear(), mrDate.getMonth(), mrDate.getDate() - 30);
      } else {
        const nextRow = data[index + 1];
        const prevEnddate = new Date(nextRow['MR Date'] as string);

        meterReading.PeriodStart = new Date(prevEnddate.getFullYear(), prevEnddate.getMonth(), prevEnddate.getDate() + 1);
      }

      this.preparedJson.push(meterReading);
    });
  }

  closeDialog() {
    this.dialog.close();
  }

  uploadDoc() {
    if (
      this.extension === "csv" ||
      this.extension === "xlsx" ||
      this.extension === "xls"
    ) {
      const providerResourceId = this.usageType === 'Electric' 
      ? this.providerResource.Electric : this.providerResource.Gas;
      const usageReadings = this.usageType === 'Electric' ? this.electricUsage : this.gasUsage;

      if (usageReadings?.length > 0) {
        Swal.fire({
          text: 'Are you sure you want to overwrite the usage data?',
          icon: "warning",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.value) {
            this.saveUsage(providerResourceId, this.preparedJson);
          }
        });
      } else {
        this.saveUsage(providerResourceId, this.preparedJson);
      } 
     } else {
        Swal.fire({
          text: !this.file ? 'No file chosen' : 'Please upload CSV/ Excel file only',
          icon: 'warning',
          showConfirmButton: false,
          timer: 2000,
        });
      }     
  }

  saveUsage(providerResourceId: string, usageReadings: UsageReadings[]) {
    this.spin = true;
        this.customerService.uploadUsage(this.customerService.currentCustomer.PersonCompanyId, providerResourceId,usageReadings)
        .subscribe(data => {
          this.spin = false;
          if (data) {
            if (!data.Action) {
              this.usageType === 'Electric' ? this.electricUsage = [...usageReadings] : this.gasUsage = [...usageReadings];
              this.jsonContent = [...usageReadings];
              Swal.fire({
                text: data.Message,
                icon: 'success',
                showConfirmButton: false,
                timer: 2000,
              });              
            } else {
              Swal.fire({
                text: data.Message,
                icon: 'error',
                showConfirmButton: false,
                timer: 2000,
              });
            }
          }
          
          this.file = null;
          this.extension = '';
        });
  }

  showUsage(usageType: string) {
    this.viewUsage = !this.viewUsage;
    this.usageType = usageType;
  
    const endpoints = {
      Electric: this.providerResource.Electric,
      Gas: this.providerResource.Gas
    };
  
    const usageData: Record<'Electric' | 'Gas', UsageReadings[]> = {
      Electric: this.electricUsage,
      Gas: this.gasUsage
    };
  
    const selectedType = this.usageType === 'Electric' ? 'Electric' : 'Gas';

    if (usageData[selectedType]?.length > 0) {
      this.jsonContent = [...usageData[selectedType]];
    } else {
      this.fetchUsageData(selectedType, endpoints[selectedType]);
    }
  }
  
  fetchUsageData(usageType: string, endpoint: string) {
    this.spin = true;  
    this.customerService.getUsage(this.customerService.currentCustomer.PersonCompanyId, endpoint)
      .subscribe(
        data => {
          this.spin = false;
          if (data) {
            if (usageType === 'Electric') {
              this.electricUsage = data;
              this.jsonContent = [...this.electricUsage];
            } else if (usageType === 'Gas') {
              this.gasUsage = data;
              this.jsonContent = [...this.gasUsage];
            }
          }
        });
  }
  
  refreshUsage(): void { 
    const accountNo = this.providerAccountNumber || this.data?.accountNumber;
 if (!accountNo) {
      return;
    }

    this.spin = true;
    this.customerService.refreshUsage(String(accountNo)).subscribe({
      next: () => {
        this.spin = false;
        Swal.fire({
          text: ` usage refreshed successfully.`,
          icon: 'success',
          showConfirmButton: false,
          timer: 2000,
        });
      },
      error: () => {
        this.spin = false;
        Swal.fire({
          text: `Unable to refresh usage.`,
          icon: 'error',
          showConfirmButton: false,
          timer: 2000,
        });
      }
    });
  }

  back() {
    this.viewUsage = !this.viewUsage;
    this.usageType = '';
    this.jsonContent = [];
    this.file = null;
    this.extension = '';
  }

  exportCurrentUsageToExcel() {
    if (this.usageType !== 'Electric' || !this.jsonContent || this.jsonContent.length === 0) {
      return;
    }

    const exportRows = this.jsonContent.map((usage: any) => ({
      'Account Number': usage.AccountNumber || this.getCurrentAccountNumber(),
      'Serial No.': usage.SerialNo,
      'Period Start': usage.PeriodStart,
      'Read Date': usage.ReadDate,
      Usage: usage.Usage,
      Demand: usage.Demand,
      Amount: usage.Amount,
      Type: usage?.Type || usage?.UsageType
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportRows);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${this.usageType || 'Meter'} Usage`);
    XLSX.writeFile(wb, `${this.usageType || 'Meter'}_Usage_Report.xlsx`);
  }
}

